import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import API from './API';
import Utils from './Utils';

// Polling interval for /admin/notifications. 30s is a reasonable trade-off
// between freshness and server load — a new booking will surface within half a
// minute for the driver.
const POLL_INTERVAL_MS = 30_000;

const NotificationsContext = createContext({
  newBookingsCount: 0,
  unseenNotificationsCount: 0,
  notifications: [],
  markOrdersSeen: () => {},
  markNotificationsSeen: () => {},
  refresh: () => {},
});

export const NotificationsProvider = ({intl, children}) => {
  const history = useHistory();
  const [newBookingsCount, setNewBookingsCount] = useState(0);
  const [unseenNotificationsCount, setUnseenNotificationsCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  // Track the highest booking id we've already surfaced as a browser push, so
  // polling doesn't re-fire the same notification each cycle.
  const lastPushedIdRef = useRef(0);
  // Skip the browser push on the very first poll (we don't want to announce
  // every pre-existing booking when the user lands on the app).
  const initializedRef = useRef(false);

  const maybeRequestPermission = useCallback(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      // Fire-and-forget; ignore the user's choice.
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const fireBrowserPush = useCallback((booking) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const title = intl ? intl.formatMessage({id: 'NEW_BOOKING'}) : 'Nouvelle réservation';
      const body = booking.customerName || '';
      const n = new Notification(title, {body, tag: `booking-${booking.id}`});
      n.onclick = () => { window.focus(); n.close(); };
    } catch (e) {
      // Safari/older browsers can throw on constructor — silently ignore.
    }
  }, [intl]);

  const fetchNotifications = useCallback(async () => {
    if (!Utils.isAccessToken(history, true)) return;
    const response = await API.getNotifications(history);
    if (!response || !response.data || response.data.success !== true) return;

    const data = response.data.data || {};
    const list = data.notifications || [];
    setNewBookingsCount(data.newBookingsCount || 0);
    setUnseenNotificationsCount(data.unseenNotificationsCount || 0);
    setNotifications(list);

    // Fire browser pushes for bookings that appeared since the previous poll.
    if (initializedRef.current && list.length > 0) {
      const highestId = Math.max(...list.map(n => n.id));
      list
        .filter(n => n.id > lastPushedIdRef.current)
        .forEach(fireBrowserPush);
      lastPushedIdRef.current = Math.max(lastPushedIdRef.current, highestId);
    } else if (list.length > 0) {
      // Seed the high-water mark so we don't push on first load.
      lastPushedIdRef.current = Math.max(...list.map(n => n.id));
    }
    initializedRef.current = true;
  }, [history, fireBrowserPush]);

  useEffect(() => {
    maybeRequestPermission();
    fetchNotifications();
    const timer = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchNotifications, maybeRequestPermission]);

  const markOrdersSeen = useCallback(() => {
    // Optimistic update — clear the badge immediately, then persist.
    setNewBookingsCount(0);
    API.postMarkOrdersSeen(history);
  }, [history]);

  const markNotificationsSeen = useCallback(() => {
    setUnseenNotificationsCount(0);
    API.postMarkNotificationsSeen(history);
  }, [history]);

  const value = useMemo(() => ({
    newBookingsCount,
    unseenNotificationsCount,
    notifications,
    markOrdersSeen,
    markNotificationsSeen,
    refresh: fetchNotifications,
  }), [newBookingsCount, unseenNotificationsCount, notifications, markOrdersSeen, markNotificationsSeen, fetchNotifications]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
